import FilterRequest = App.Http.Requests.FilterRequest;
import PageRequest = App.Http.Requests.PageRequest;

interface SelectOption {
    label: string;
    value: string | number;
}

interface CacheEntry<TData> {
    data: TData[];
    timestamp: number;
    options: SelectOption[];
}

interface QueryParams {
    filter?: FilterRequest;
    page?: PageRequest;
}

type CacheListener = () => void;

// Stable JSON stringify with sorted keys (prevents cache misses from key ordering)
const stableStringify = (obj: unknown): string => {
    if (obj === null || typeof obj !== 'object') {
        return JSON.stringify(obj);
    }
    /* v8 ignore next 3 -- array case rarely hit in practice */
    if (Array.isArray(obj)) {
        return '[' + obj.map(stableStringify).join(',') + ']';
    }
    const keys = Object.keys(obj).sort();
    return '{' + keys.map(k => `${JSON.stringify(k)}:${stableStringify((obj as Record<string, unknown>)[k])}`).join(',') + '}';
};

class SelectOptionsCache {
    private cache = new Map<string, CacheEntry<unknown>>();
    private pendingRequests = new Map<string, Set<CacheListener>>();
    private refCounts = new Map<string, number>();

    createCacheKey(cacheKey: string, queryParams: QueryParams): string {
        const paramsStr = stableStringify(queryParams);
        return `${cacheKey}_${paramsStr}`;
    }

    // Increment ref count when component mounts
    addRef(cacheKey: string): void {
        this.refCounts.set(cacheKey, (this.refCounts.get(cacheKey) ?? 0) + 1);
    }

    // Decrement ref count when component unmounts, cleanup if no refs left
    removeRef(cacheKey: string): void {
        const count = (this.refCounts.get(cacheKey) ?? 1) - 1;
        if (count <= 0) {
            this.refCounts.delete(cacheKey);
            this.cache.delete(cacheKey);
            this.pendingRequests.delete(cacheKey);
        } else {
            this.refCounts.set(cacheKey, count);
        }
    }

    getCachedData<TData>(cacheKey: string): CacheEntry<TData> | null {
        const cached = this.cache.get(cacheKey) as CacheEntry<TData> | undefined;
        if (cached) {
            return cached;
        }
        return null;
    }

    setCachedData<TData>(cacheKey: string, data: TData[], options: SelectOption[]): void {
        this.cache.set(cacheKey, {
            data,
            timestamp: Date.now(),
            options,
        });
        // Notify all waiting listeners that data is ready
        this.notifyListeners(cacheKey);
    }

    // Atomically check and register - returns true if this caller should fetch
    tryRegisterPendingRequest(cacheKey: string): "registered" | "already-pending" {
        if (this.pendingRequests.has(cacheKey)) {
            return "already-pending";
        }
        this.pendingRequests.set(cacheKey, new Set());
        return "registered";
    }

    // Subscribe to be notified when data is ready
    subscribe(cacheKey: string, listener: CacheListener): () => void {
        // Check if data arrived between registration check and subscribe call
        if (this.cache.has(cacheKey)) {
            // Data is ready, notify immediately
            setTimeout(listener, 0);
            return () => undefined; // No-op unsubscribe
        }

        const listeners = this.pendingRequests.get(cacheKey);
        if (listeners) {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        }

        /* v8 ignore next 3 -- error edge case when pending request removed */
        // Pending request was removed (error case) - notify immediately so component retries
        setTimeout(listener, 0);
        return () => undefined;
    }

    // Cancel pending request (e.g., on error)
    cancelPendingRequest(cacheKey: string): void {
        this.notifyListeners(cacheKey);
        this.pendingRequests.delete(cacheKey);
    }

    // Invalidate cache entry (for manual refetch)
    invalidate(cacheKey: string): void {
        this.notifyListeners(cacheKey);
        this.cache.delete(cacheKey);
        this.pendingRequests.delete(cacheKey);
    }

    private notifyListeners(cacheKey: string): void {
        const listeners = this.pendingRequests.get(cacheKey);
        if (listeners) {
            listeners.forEach(listener => listener());
            this.pendingRequests.delete(cacheKey);
        }
    }
}

// Export singleton instance
export const selectOptionsCache = new SelectOptionsCache();